import type { ConversationParticipant, ConversationSummary, MessageFlag, MessageSummary } from '@/api/types';

/**
 * A conversation, as the list's row (ADR 0077, plan/16 §8).
 *
 * The list, the keymap, the menus and every verb were built around
 * `MessageSummary`, and a conversation row has to go through all of them. So
 * it IS one — the summary of its newest message (the head), which is what
 * opening, replying and Add to Tasks target — with the conversation attached.
 * Two things tell it apart from a message row, and both are deliberate:
 *
 * - its row key is the CONVERSATION's (`rowKey.ts`), so the cursor and the
 *   selection stay on it when new mail changes its head;
 * - every verb expands it to its members (`expandConversations`) before it
 *   reaches the batched mutation hooks.
 */
export interface ConversationRow extends MessageSummary {
  conversation: ConversationSummary;
}

export function isConversationRow(message: MessageSummary): message is ConversationRow {
  return (message as Partial<ConversationRow>).conversation !== undefined;
}

function withoutSeen(flags: readonly MessageFlag[]): MessageFlag[] {
  return flags.filter((flag) => flag !== 'seen');
}

function withSeen(flags: readonly MessageFlag[]): MessageFlag[] {
  return flags.includes('seen') ? [...flags] : [...flags, 'seen'];
}

/**
 * Rows are built once per summary object. The cache hands back the same
 * summary until a patch or a sync replaces it, so the virtualized list sees
 * the same row object and does not re-render a row that did not change.
 */
const rows = new WeakMap<ConversationSummary, ConversationRow>();

export function toConversationRow(summary: ConversationSummary): ConversationRow {
  const cached = rows.get(summary);
  if (cached) return cached;
  const sender = summary.participants[summary.participants.length - 1];
  const row: ConversationRow = {
    id: summary.newestMessageId,
    accountId: summary.accountId,
    folderId: summary.folderId,
    threadId: summary.id,
    subject: summary.subject,
    from: sender
      ? { ...(sender.name ? { name: sender.name } : {}), email: sender.email }
      : { email: '' },
    to: [],
    date: summary.date,
    snippet: summary.snippet,
    // Unread while ANY member is: the row's bold is the conversation's.
    flags: summary.unreadCount > 0 ? withoutSeen(summary.flags) : withSeen(summary.flags),
    hasAttachments: summary.hasAttachments,
    threadCount: summary.messageCount,
    sizeBytes: 0,
    // The union over every message, merged Sent included (plan/17 §4): the
    // row's chips, and what the picker counts as "on" for this row.
    labelKeywords: summary.labelKeywords ?? [],
    conversation: summary,
  };
  rows.set(summary, row);
  return row;
}

/**
 * The members of a conversation row, as the rows the mutation hooks take.
 *
 * In-folder members only (`messageIds`): a merged Sent message is not in this
 * folder, and archiving the Inbox's conversation must not archive Sent mail.
 * The row knows HOW MANY members are unread, not which, and the hooks read
 * `seen` only to count the folder's unread total — so the newest `unreadCount`
 * stubs are unread and the counts come out exact. No stub's flags are ever
 * written anywhere: the flag patch names ids and says what changed.
 */
export function conversationMembers(row: ConversationRow): MessageSummary[] {
  const { conversation, ...head } = row;
  const ids = conversation.messageIds;
  const firstUnread = ids.length - Math.min(conversation.unreadCount, ids.length);
  return ids.map((id, index) => ({
    ...head,
    id,
    flags: index >= firstUnread ? withoutSeen(head.flags) : withSeen(head.flags),
  }));
}

/**
 * A verb's variables, with every conversation row replaced by its members and
 * the number of conversations noted for the Undo's sentence. Message rows
 * pass through untouched, and variables with no conversation in them come
 * back as the same object — the flat list's calls are exactly what they were.
 */
export function expandConversations<T extends { messages: MessageSummary[] }>(
  variables: T,
): T & { conversations?: number } {
  const count = variables.messages.filter(isConversationRow).length;
  if (count === 0) return variables;
  return {
    ...variables,
    messages: variables.messages.flatMap((message) =>
      isConversationRow(message) ? conversationMembers(message) : [message],
    ),
    conversations: count,
  };
}

/**
 * The ids a LABEL verb reaches for these rows (plan/17 §5): a conversation
 * row is every member in the folder plus its merged Sent messages — a label
 * is on the conversation, wherever its messages live — and a message row is
 * itself.
 */
export function labelTargetIds(rows: readonly MessageSummary[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const own = isConversationRow(row)
      ? [...row.conversation.messageIds, ...(row.conversation.sentMessageIds ?? [])]
      : [row.id];
    for (const id of own) {
      if (seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/** Whether a message id belongs to this conversation row (any member). */
export function conversationHolds(row: ConversationRow, messageId: string): boolean {
  return row.id === messageId || row.conversation.messageIds.includes(messageId);
}

/* ------------------------------------------------------------ participants */

/** One name in the participants line, or the ".." that stands for the middle. */
export type ParticipantPart =
  | { kind: 'name'; label: string; unread: boolean; title: string }
  | { kind: 'gap' };

function shortName(participant: ConversationParticipant): string {
  if (participant.isMe) return 'me';
  const name = participant.name?.trim();
  if (name) return name.split(/\s+/)[0]!;
  return participant.email.split('@')[0] ?? participant.email;
}

function fullName(participant: ConversationParticipant): string {
  if (participant.isMe) return 'me';
  return participant.name?.trim() || participant.email;
}

/**
 * Gmail's participants line: "me, Ana, Bob", with the middle of a long one
 * collapsed ("Ana .. Bob, Cy"). One participant is named in full; several go
 * by first names, which is what fits. The count follows separately.
 */
export function participantParts(participants: readonly ConversationParticipant[]): ParticipantPart[] {
  if (participants.length === 0) return [];
  const name = (participant: ConversationParticipant, full: boolean): ParticipantPart => ({
    kind: 'name',
    label: full ? fullName(participant) : shortName(participant),
    unread: participant.unread,
    title: participant.isMe ? 'me' : participant.email,
  });
  if (participants.length === 1) return [name(participants[0]!, true)];
  if (participants.length <= 3) return participants.map((participant) => name(participant, false));
  return [
    name(participants[0]!, false),
    { kind: 'gap' },
    name(participants[participants.length - 2]!, false),
    name(participants[participants.length - 1]!, false),
  ];
}

/** The same line as plain text, for the row's accessible sentence. */
export function participantsText(participants: readonly ConversationParticipant[]): string {
  return participants.map(fullName).join(', ');
}
